// kanjo/COGS.jsx — Food cost dashboard wired to live data (US-4.3).
// Style aligned with the mockup design system.

// ── FoodCostGauge: 180° arc with target tick + colored fill ──────────────
function FoodCostGauge({ value, target = 28, label = 'Food cost' }) {
  // Strip floating-point noise (0.28 * 100 = 28.000000000000004) once so it
  // doesn't leak into the on-gauge label, aria-label, or tooltip.
  const tgt = Math.round((target + Number.EPSILON) * 10) / 10;
  // Scale runs 0 → 150% of target; danger zone painted on the track above 35%.
  const MIN = 0;
  const MAX = Math.round(tgt * 1.5);
  const DANGER = 35;
  const w = 220, h = 130;
  const cx = w / 2, cy = h - 14;
  const r = 88;
  const v = value == null ? null : Math.max(MIN, Math.min(MAX, value));
  const toAngle = (val) => Math.PI - ((val - MIN) / (MAX - MIN)) * Math.PI;
  const pt = (val, rad = r) => {
    const a = toAngle(val);
    return { x: cx + rad * Math.cos(a), y: cy - rad * Math.sin(a) };
  };
  const arc = (from, to, rad = r) => {
    const A = pt(from, rad), B = pt(to, rad);
    const large = Math.abs(toAngle(to) - toAngle(from)) > Math.PI ? 1 : 0;
    return `M ${A.x.toFixed(1)} ${A.y.toFixed(1)} A ${rad} ${rad} 0 ${large} 1 ${B.x.toFixed(1)} ${B.y.toFixed(1)}`;
  };
  const trackColor = 'var(--surface-3)';
  const fillColor = v == null ? trackColor :
    v <= tgt ? 'var(--positive)' :
    v < DANGER ? 'var(--warning)' : 'var(--danger)';
  const tgtOut = pt(tgt, r + 6);
  const tgtIn = pt(tgt, r - 8);
  return (
    <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="xMidYMax meet"
         role="img" aria-label={v == null ? `No ${label.toLowerCase()} data` : `${label} ${v.toFixed(1)}% versus ${tgt}% target`}>
      <title>{v == null ? `No ${label.toLowerCase()} data` : `${label} ${v.toFixed(1)}% vs ${tgt}% target`}</title>
      <path d={arc(MIN, MAX)} fill="none" stroke={trackColor} strokeWidth="14" strokeLinecap="round"/>
      {DANGER < MAX && (
        <path d={arc(DANGER, MAX)} fill="none" stroke="var(--danger)" strokeWidth="14" strokeLinecap="round" opacity="0.35"/>
      )}
      {v != null && <path d={arc(MIN, v)} fill="none" stroke={fillColor} strokeWidth="14" strokeLinecap="round"/>}
      <line x1={tgtIn.x} y1={tgtIn.y} x2={tgtOut.x} y2={tgtOut.y} stroke="var(--ink-2)" strokeWidth="1.5" strokeLinecap="round"/>
      <text x={tgtOut.x} y={tgtOut.y - 4} textAnchor="middle" fontSize="10" fill="var(--ink-3)" fontFamily="var(--mono)">{tgt}%</text>
      <text x={pt(MIN, r + 14).x} y={pt(MIN, r + 14).y + 4} textAnchor="middle" fontSize="10" fill="var(--ink-4)" fontFamily="var(--mono)">{MIN}</text>
      <text x={pt(MAX, r + 14).x} y={pt(MAX, r + 14).y + 4} textAnchor="middle" fontSize="10" fill="var(--ink-4)" fontFamily="var(--mono)">{MAX}</text>
    </svg>
  );
}

function fmtMoneyG(cents, currency, locale) {
  const c = window.kanjo?.demo?.scale ? window.kanjo.demo.scale(cents) : cents;
  return new Intl.NumberFormat(locale || 'en-US', { style: 'currency', currency: currency || 'USD' })
    .format((c || 0) / 100);
}
function fmtPctG(p) { return p == null ? '—' : `${(p * 100).toFixed(1)}%`; }

function useCogsData(initialPeriod = 'month') {
  const [state, setState] = React.useState({ loading: true, period: initialPeriod });
  const setPeriod = React.useCallback((p) => setState((s) => ({ ...s, period: p, loading: true })), []);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!window.kanjo) return;
      try {
        const claims = await window.kanjo.auth.currentClaims().catch(() => null);
        if (!claims) { setState((s) => ({ ...s, loading: false })); return; }
        const profile = await window.kanjo.db.userProfile.get(claims.sub);
        const today = localToday();
        let from = today;
        let to = today;
        if (state.period === 'week') {
          const d = new Date(today + 'T00:00:00Z'); d.setUTCDate(d.getUTCDate() - 6);
          from = d.toISOString().slice(0, 10);
        } else if (state.period === 'month') {
          const d = new Date(today + 'T00:00:00Z'); d.setUTCDate(1);
          from = d.toISOString().slice(0, 10);
        } else if (state.period === 'lastMonth') {
          const d = new Date(today + 'T00:00:00Z'); d.setUTCDate(1);
          const end = new Date(d); end.setUTCDate(0);          // last day of previous month
          const start = new Date(end); start.setUTCDate(1);    // first day of previous month
          from = start.toISOString().slice(0, 10);
          to = end.toISOString().slice(0, 10);
        } else if (state.period === 'year') {
          const d = new Date(today + 'T00:00:00Z'); d.setUTCMonth(0, 1);
          from = d.toISOString().slice(0, 10);
        }
        const targetPct = profile?.foodCostTargetPct ?? 0.28;
        // Calendar-month periods settle the sales denominator to the month the
        // sales were EARNED (matches the Today screen); the rolling 'week'
        // window stays deposit-dated.
        const settled = state.period !== 'week';
        const [calc, trend] = await Promise.all([
          window.kanjo.revenue.cogs.calc({ from, to, targetPct, settled }),
          window.kanjo.revenue.cogs.trendWeekly({ weeks: 12, targetPct }),
        ]);
        if (!cancelled) setState({
          loading: false, period: state.period,
          calc, trend, targetPct,
          currency: profile?.currency || 'USD',
          locale: profile?.locale || 'en-US',
        });
      } catch (e) { console.warn('[COGS] load failed', e); if (!cancelled) setState((s) => ({ ...s, loading: false })); }
    })();
  }, [state.period]);
  return [state, setPeriod];
}

function COGSDashboard({ dark = false, persona = 'owner' }) {
  const urlPeriod = useHashQuery('period');
  const initial = ['week', 'month', 'lastMonth', 'year'].includes(urlPeriod) ? urlPeriod : 'month';
  const [s, setPeriod] = useCogsData(initial);
  // Weekly-trend metric: 'sales' (cogs ÷ recognized sales) or 'deposits' (cogs ÷
  // all bank inflows that week — a rougher reference that doesn't depend on
  // channel mapping). The target color stays the same; only the bar value swaps.
  const [trendMetric, setTrendMetric] = React.useState('sales');

  if (s.loading || !s.calc) return (
    <KShell dark={dark} tab="reports" persona={persona}>
      <KHeader back big title="Food cost" />
      <div style={{ padding: '0 20px' }}>
        <SkeletonCard height={180}/>
      </div>
    </KShell>
  );

  const c = s.calc;
  const pctValue = c.foodCostPct == null ? null : c.foodCostPct * 100;
  const targetValue = (c.targetPct ?? 0.28) * 100;
  const statusLabel =
    c.status === 'green' ? 'On target' :
    c.status === 'amber' ? 'Watching'  :
    c.status === 'red'   ? 'Over target' : '—';
  const statusPill =
    c.status === 'green' ? 'positive' :
    c.status === 'amber' ? 'warning'  :
    c.status === 'red'   ? 'danger'   : 'neutral';
  const periodLabel =
    s.period === 'week'      ? 'this week'  :
    s.period === 'lastMonth' ? 'last month' :
    s.period === 'year'      ? 'this year'  : 'this month';

  return (
    <KShell dark={dark} tab="reports" persona={persona}>
      <div style={{ height: '100%', overflowY: 'auto', paddingBottom: 16 }}>
        <KHeader back big title="Food cost" subtitle={`${c.from} → ${c.to}`} />

        {/* Period selector */}
        <div style={{ padding: '0 20px 18px' }}>
          <div style={{ display: 'flex', gap: 4, padding: 4, background: 'var(--surface-3)', borderRadius: 12 }}>
            {[
              { id: 'month',     l: 'This month' },
              { id: 'lastMonth', l: 'Last month' },
              { id: 'year',      l: 'This year' },
            ].map((t) => (
              <button key={t.id} onClick={() => setPeriod(t.id)} style={{
                flex: 1, padding: '8px 10px', borderRadius: 8, border: 'none',
                background: s.period === t.id ? 'var(--surface)' : 'transparent',
                fontSize: 13, fontWeight: s.period === t.id ? 600 : 500,
                color: s.period === t.id ? 'var(--ink)' : 'var(--ink-3)',
                cursor: 'pointer',
                boxShadow: s.period === t.id ? '0 1px 2px rgba(0,0,0,0.04)' : 'none',
              }}>{t.l}</button>
            ))}
          </div>
        </div>

        {/* Hero gauge */}
        <div style={{ padding: '0 20px 14px' }}>
          <div className="card" style={{ padding: 22 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
              <div className="label">Food cost · {periodLabel}</div>
              <span className={`pill ${statusPill}`}>{statusLabel}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 18, marginTop: 12 }}>
              <div style={{ flexShrink: 0, width: 160 }}>
                <FoodCostGauge value={pctValue} target={targetValue}/>
              </div>
              <div style={{ flex: 1, minWidth: 0, paddingBottom: 8 }}>
                {pctValue == null ? (
                  <div className="figure xl">—</div>
                ) : (
                  <KPct value={pctValue} size="xl"
                        style={{ fontSize: 56,
                          color: c.status === 'red' ? 'var(--danger)' :
                                 c.status === 'amber' ? 'var(--warning)' : 'var(--ink)' }}/>
                )}
                <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 4 }}>
                  vs <KPct value={targetValue} style={{ fontSize: 13, color: 'var(--ink-2)', fontWeight: 500 }}/> target
                </div>
              </div>
            </div>
            <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--rule)',
                          display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--ink-2)' }}>
              <div>
                <div className="label" style={{ marginBottom: 4 }}>COGS</div>
                <div className="mono" style={{ fontWeight: 500 }}>{fmtMoneyG(c.cogsCents, s.currency, s.locale)}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div className="label" style={{ marginBottom: 4 }}>{s.period === 'week' ? 'Net sales' : 'Net sales · settled'}</div>
                <div className="mono" style={{ fontWeight: 500 }}>{fmtMoneyG(c.netSalesCents, s.currency, s.locale)}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Cost breakdown by category */}
        <div style={{ padding: '0 20px 14px' }}>
          <div className="label" style={{ marginBottom: 10, paddingLeft: 4 }}>Cost breakdown</div>
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            {[
              ['Food', (c.byCategory && c.byCategory.food) || 0, true],
              ['Beverages', (c.byCategory && c.byCategory.beverages) || 0, true],
              ['Packaging', (c.byCategory && c.byCategory.packaging) || 0, true],
              ['Other', (c.byCategory && c.byCategory.other) || 0, false],
            ].map(([label, cents, inCogs], i, a) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '13px 16px', borderBottom: i < a.length - 1 ? '1px solid var(--rule)' : 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 4, background: inCogs ? 'var(--brand)' : 'var(--ink-4)' }}/>
                  <span style={{ fontSize: 14, fontWeight: 500, color: inCogs ? 'var(--ink)' : 'var(--ink-3)' }}>{label}{inCogs ? '' : ' · not COGS'}</span>
                </div>
                <span className="mono" style={{ fontSize: 14, fontWeight: 500, color: inCogs ? 'var(--ink)' : 'var(--ink-3)' }}>{fmtMoneyG(cents, s.currency, s.locale)}</span>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--on-dim)', marginTop: 6, paddingLeft: 4 }}>COGS = Food + Beverages + Packaging. Other is tracked but excluded.</div>
        </div>

        {/* Weekly trend — one bar per ISO week (Mon→Sun); the last bar is week-to-date.
            Toggle between cogs/sales (the food-cost %) and cogs/deposits (a rougher
            reference that absorbs the channel-mapping noise). */}
        <div style={{ padding: '0 20px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, paddingLeft: 4 }}>
            <div className="label">Weekly trend · last {s.trend.length} weeks</div>
            <div role="tablist" aria-label="Trend denominator" style={{ display: 'flex', gap: 4, background: 'var(--surface-3,#f0f0f3)', borderRadius: 8, padding: 3 }}>
              {[['sales', 'vs Sales'], ['deposits', 'vs Deposits']].map(([k, lbl]) => {
                const on = trendMetric === k;
                return (
                  <button key={k} role="tab" aria-selected={on} onClick={() => setTrendMetric(k)}
                    style={{ padding: '5px 10px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 11.5, fontWeight: 600,
                      background: on ? 'var(--surface,#fff)' : 'transparent', color: on ? 'var(--ink)' : 'var(--ink-3)',
                      boxShadow: on ? '0 1px 2px rgba(0,0,0,0.06)' : 'none' }}>{lbl}</button>
                );
              })}
            </div>
          </div>
          <div className="card" style={{ padding: 16 }}>
            <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', height: 72 }}>
              {s.trend.map((w) => {
                const ratio = trendMetric === 'deposits' ? w.depositPct : w.foodCostPct;
                const pct = (ratio || 0) * 100;
                const overBy = pct - targetValue;
                const color = pct === 0 ? 'var(--surface-3)' :
                              overBy <= 0 ? 'var(--positive)' :
                              overBy <= 5 ? 'var(--warning)' : 'var(--danger)';
                const h = Math.max(2, Math.round((pct / 50) * 64));
                const partialTag = w.partial ? ' (week-to-date)' : '';
                const title = trendMetric === 'deposits'
                  ? `Week of ${w.weekStart} — ${fmtPctG(ratio)} (COGS ÷ deposits)${partialTag}`
                  : `Week of ${w.weekStart} — ${fmtPctG(ratio)} (COGS ÷ sales)${partialTag}`;
                return <div key={w.weekStart} title={title}
                            style={{ flex: 1, height: h, borderRadius: 3, background: color, opacity: w.partial ? 0.55 : 1 }} />;
              })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 11, color: 'var(--ink-3)' }}>
              <span>week of {s.trend[0]?.weekStart || ''}</span>
              <span>this week</span>
            </div>
            {/* Legend — what the colors actually mean. */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 10, fontSize: 11, color: 'var(--ink-3)' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--positive)' }}/>at or under target</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--warning)' }}/>up to +5pp over</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--danger)' }}/>more than +5pp over</span>
            </div>
            {trendMetric === 'deposits' && (
              <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 6 }}>
                COGS ÷ all bank deposits that week. Not directly comparable to the food-cost target — it's a rougher reference that doesn't depend on how channels are mapped.
              </div>
            )}
          </div>
        </div>

        {/* Top contributors */}
        {c.drilldown.length > 0 && (
          <div style={{ padding: '0 20px 14px' }}>
            <div className="label" style={{ marginBottom: 10, paddingLeft: 4 }}>Top contributors</div>
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              {c.drilldown.slice(0, 10).map((r, i, a) => (
                <div key={r.itemId} style={{
                  display: 'flex', justifyContent: 'space-between', padding: '14px 16px',
                  borderBottom: i < a.length - 1 ? '1px solid var(--rule)' : 'none', alignItems: 'center',
                }}>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>{r.itemName}</div>
                  <div className="mono" style={{ fontSize: 14, fontWeight: 500 }}>{fmtMoneyG(r.totalCents, s.currency, s.locale)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </KShell>
  );
}

Object.assign(window, { COGSDashboard, FoodCostGauge });
